<template>
  <v-row no-gutters class="pt-5">
    <v-col class="pr-2">
      <card-title-row translation-path="sidebar.buffer.title" :has-buttons="false" />
      <v-form ref="bufferForm" v-model="isValid">
        <sidebar-row class="pt-5">
          <v-row no-gutters class="justify-space-between">
            <v-col cols="4" class="text-body-2 center-content text-left pt-8">
              {{ $i18n.t('sidebar.buffer.bufferValueM') }}:
            </v-col>
            <v-col cols="7">
              <v-row>
                <v-col cols="12">
                  <v-radio-group
                    class="my-0 py-0 input--type"
                    v-model="bufferPayload.type"
                    :rules="bufferRules"
                    dense
                    row
                    hide-details
                    @change="bufferPayload.value = ''"
                    :readonly="isStickyButtonsLoading"
                  >
                    <v-radio :label="$i18n.t('dialog.value')" value="buffer"></v-radio>
                    <v-radio :label="$i18n.t('dialog.attribute')" value="buffer_field"></v-radio>
                  </v-radio-group>
                </v-col>
              </v-row>
              <v-row no-gutters style="width: 100%">
                <v-col>
                  <data-input
                    v-bind="bufferValueConfig"
                    :height="20"
                    v-model="bufferPayload.value"
                    :rules="bufferRules"
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col cols="12">
              <v-checkbox v-model="bufferPayload.dissolve">
                <template #label>
                  <div class="text-body-2 text-left black--text">
                    {{ $i18n.t('sidebar.buffer.dissolve') }}
                  </div>
                </template>
              </v-checkbox>
            </v-col>
          </v-row>
        </sidebar-row>
      </v-form>
      <sidebar-row v-if="bufferDone">
        <v-row no-gutters style="gap: 1rem">
          <v-col cols="auto">
            <dots-menu :items="dotsItems" @toShpFile="saveBuffer('shp')" @toDataSource="openDataSourceModal">
              <template #activator="{ on }">
                <base-button
                  class="mr-0"
                  v-on="on"
                  :straight-text="$i18n.t('button.saveResult')"
                  :loading="loading && !isDataSourceModalVisible"
                />
              </template>
            </dots-menu>
          </v-col>
          <v-col>
            <dots-menu :items="copyGeometryDotsItems" @toSearchArea="openSearchArea">
              <template #activator="{ on }">
                <base-button-subheader v-on="on" translation-path="sidebar.buffer.runInTool" cancel />
              </template>
            </dots-menu>
          </v-col>
        </v-row>
      </sidebar-row>
    </v-col>
    <main-input-dialog
      v-if="isDataSourceModalLoaded"
      :is-visible.sync="isDataSourceModalVisible"
      :loading="loading"
      :title-translation="$i18n.t('dialog.title.saveResultToDataSource')"
      dialog-width="30%"
      @agree="saveBuffer('datasource')"
      :inputs="dataSourceModalInputs"
    >
      <template #dataSourceName.input="data">
        <v-col cols="6">
          <data-input v-model="bufferDataSourceName" v-bind="data" />
        </v-col>
      </template>
    </main-input-dialog>
  </v-row>
</template>
<script>
import { call, get, sync } from 'vuex-pathify';

import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
import MainInputDialog from '@/components/MainInputDialog';
import SidebarRow from '@/components/SidebarRow';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'Buffer',
  components: { CardTitleRow, DataInput, DotsMenu, MainInputDialog, SidebarRow },
  mixins: [prepareAttributesFeatureCardMixin],
  computed: {
    currentLayer: get('layers/currentLayer'),
    dataSources: get('layers/metadata'),
    dataSourcesArray() {
      return this.$_objectToArray(this.dataSources).filter(
        dataSource => dataSource.geometry_type === 'polygon' || dataSource.geometry_type === 'multipolygon'
      );
    },

    bufferDataSource: get('layers/metadata@:bufferLayerDataSourceName'),
    bufferLayer: get('layers/currentLayer'),
    bufferLayerDataSourceName: get('layers/currentLayer@data_source_name'),
    bufferLayerId: get('layers/currentLayer@id'),
    bufferLayerSelectedFeatures: get('map/selectedFeatures@:bufferLayerId'),
    bufferLayerAttributes() {
      return this.$_flattenStructure(
        this.getForm(
          this.bufferLayer?.form_schema?.elements || [],
          this.bufferDataSource?.attributes_schema?.attributes || []
        )
      ).filter(attribute => ['integer', 'decimal', 'float'].includes(attribute.dataType?.name));
    },
    bufferRules() {
      return [v => !!v || v === false || v === 0 || this.$i18n.t('rules.required')];
    },
    bufferValueConfig() {
      return this.bufferPayload.type === 'buffer_field'
        ? {
            itemText: 'label',
            itemValue: 'attribute',
            items: this.bufferLayerAttributes,
            label: this.$i18n.t('dialog.body.attribute'),
            dataType: { name: 'select' },
            readOnly: this.isStickyButtonsLoading,
          }
        : {
            label: this.$i18n.t('dialog.body.value'),
            dataType: { name: 'float' },
            readOnly: this.isStickyButtonsLoading,
          };
    },

    dataSourceModalInputs() {
      return [
        {
          name: 'dataSourceName',
          translationPath: 'dialog.dataSource',
          subtitle: 'sidebar.buffer.dataSourceSubtitle',
          dataType: {
            name: 'select',
          },
          items: this.dataSourcesArray,
          itemValue: 'name',
          itemText: 'verbose_name',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
      ];
    },

    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    stickyButtons: sync('sidebar/stickyButtons'),

    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
  },
  data: () => ({
    bufferDataSourceName: '',
    bufferDone: false,
    bufferDonePayload: undefined,
    bufferPayload: {
      value: '',
      type: 'buffer',
      dissolve: false,
    },
    dotsItems: [{ name: 'toShpFile' }, { name: 'toDataSource' }],
    copyGeometryDotsItems: [{ name: 'toSearchArea' }],
    bufferResultGeojson: null,
    isDataSourceModalLoaded: false,
    isDataSourceModalVisible: false,
    isValid: false,
    loading: false,
  }),
  methods: {
    buffer: call('edit/buffer'),
    getPayload(format = 'geojson', oldPayload = {}) {
      const payload = {
        features_id: oldPayload['features_id'] || this.bufferLayerSelectedFeatures || [],
        dissolve: this.bufferPayload.dissolve,
        format,
      };
      if (oldPayload['buffer']) {
        payload['buffer'] = oldPayload['buffer'];
      } else if (oldPayload['buffer_field']) {
        payload['buffer_field'] = oldPayload['buffer_field'];
      } else {
        payload[this.bufferPayload.type] = this.bufferPayload.value;
      }
      if (format === 'datasource') {
        payload['datasource_name'] = oldPayload['datasource_name'] || this.bufferDataSourceName;
      }
      return payload;
    },
    openSearchArea() {
      this.$store.set('moduleSharing/SET_SHAREABLE_DATA!', {
        tool: 'buffer',
        destinationTool: 'searchArea',
        geometry: JSON.stringify(this.bufferResultGeojson),
      });
    },
    openDataSourceModal() {
      this.isDataSourceModalLoaded = true;
      this.isDataSourceModalVisible = true;
    },
    resetBuffer() {
      this.$root.$emit('toggleCustomVectorLayer', { id: 'buffer' });
      this.bufferPayload = {
        value: '',
        type: 'buffer',
        dissolve: false,
      };
      this.$refs['bufferForm']?.resetValidation();
      this.bufferDonePayload = undefined;
      this.bufferDone = false;
    },
    async runBuffer() {
      this.$root.$emit('toggleCustomVectorLayer', { id: 'buffer' });
      this.isStickyButtonsLoading = true;
      try {
        const payload = this.getPayload();
        const data = { payload, layerId: this.bufferLayerId };
        const r = await this.buffer(data);
        const features = r.data.data;
        this.bufferResultGeojson = features;
        this.$root.$emit('toggleCustomVectorLayer', {
          id: 'buffer',
          geometry: features,
          fitView: true,
        });
        this.bufferDonePayload = JSON.parse(JSON.stringify(payload));
        this.bufferDone = true;
        this.isStickyButtonsLoading = false;
      } catch {
        this.bufferDonePayload = undefined;
        this.isStickyButtonsLoading = false;
      }
    },
    async saveBuffer(format) {
      this.loading = true;
      try {
        const payload = this.getPayload(format, this.bufferDonePayload);
        const data = { payload, layerId: this.bufferLayerId };
        await this.buffer(data);
        if (format === 'datasource') {
          if (this.currentLayer?.data_source_name === this.bufferDataSourceName) {
            this.$root.$emit('refreshTableData');
          }
          const layersIds = [];
          this.projectLayers.forEach(layer => {
            if (layer.data_source_name === this.bufferDataSourceName) {
              layersIds.push(layer.id);
              this.$root.$emit('refreshMvtSource', layer.id);
            }
          });
          this.$root.$emit('updateLegendStylesCounts', { layersIds });
        }
        this.isDataSourceModalVisible = false;
        this.bufferDataSourceName = '';
        this.loading = false;
      } catch {
        this.loading = false;
      }
    },
  },
  watch: {
    bufferDone: {
      immediate: true,
      handler(nV) {
        if (nV) {
          this.stickyButtons = [
            {
              action: 'runBuffer',
              translationPath: 'dialog.agreeRunAgain',
            },
            {
              action: 'resetBuffer',
              translationPath: 'dialog.reset',
              cancel: true,
              preventGoToLegendCancel: true,
            },
          ];
        } else {
          this.stickyButtons = [
            {
              action: 'runBuffer',
              translationPath: 'dialog.agreeRun',
            },
          ];
        }
      },
    },
  },
  mounted() {
    this.$watch(
      vm => {
        return { formValid: vm.isValid, selectedFeatures: vm.bufferLayerSelectedFeatures };
      },
      nV => {
        const { formValid, selectedFeatures } = nV;
        this.isStickyButtonsValid =
          formValid && selectedFeatures?.length && selectedFeatures?.length <= 5000 ? true : false;
      }
    );
    this.$root.$off('runBuffer');
    this.$root.$on('runBuffer', this.runBuffer);
    this.$root.$off('resetBuffer');
    this.$root.$on('resetBuffer', this.resetBuffer);
    this.stickyButtons = [
      {
        action: 'runBuffer',
        translationPath: 'dialog.agreeRun',
      },
    ];
    this.isStickyButtonsLoading = false;
    this.isStickyButtonsVisible = true;
  },
  beforeDestroy() {
    this.$root.$emit('toggleCustomVectorLayer', { id: 'buffer' });
    this.isStickyButtonsVisible = false;
    this.stickyButtons = [];
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .input--type {
    .v-icon {
      font-size: 15px;
    }
    .v-label {
      font-size: 12px;
    }
    .v-input--selection-controls__ripple {
      width: 15px;
      height: 15px;
      left: -3px;
      top: calc(50% - 15px) !important;
    }
    .v-input--selection-controls__input {
      margin-right: 0;
    }
  }
}
</style>
